import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    UserCircle,
    BookOpen,
    PenTool,
    FileText,
    HelpCircle,
    LogOut,
    X,
    Target,
    Globe,
    Mic,
    BookMarked,
    ExternalLink,
    ChevronRight,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../utils/AuthContext";
import { getTestPortalUrl, buildSsoUrl, getLogoutUrl } from "../../utils/sso";
import api from "../../utils/api";

const DashboardLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { user, logout } = useAuth();

    const handleLogout = async () => {
        // Invalidate server token (Laravel personal_access_tokens) so replay fails
        try {
            await api.post("/logout");
        } catch {}
        logout(); // clear local sessionStorage
        // Reliable logout: navigate through test-portal /sso/clear which destroys the
        // Cake Student session + sso_token cookie (top-level nav, not iframe), then back to login.
        const loginUrl = `${window.location.origin}/login`;
        window.location.assign(getLogoutUrl(loginUrl));
    };

    const handleTestPortalClick = (e) => {
        e.preventDefault();
        const token = user?.token;
        const url = token ? buildSsoUrl(token, "/") : getTestPortalUrl("/");
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleOverlayClick = () => {
        setIsMenuOpen(false);
    };

    // Navigation Links Configuration
    const navLinks = [
        {
            path: "/dashboard/profile",
            icon: <UserCircle size={18} />,
            label: "Profile",
        },
        {
            path: "https://test.pyqpedia.com/",
            icon: <BookOpen size={18} />,
            label: "Test Series",
            external: true,
        },
        {
            path: "/dashboard/mocks-subjective",
            icon: <FileText size={18} />,
            label: "Mock Subjective",
        },
        {
            path: "/dashboard/study-material",
            icon: <BookMarked size={18} />,
            label: "Study Material",
        },
        {
            path: "/dashboard/answer-writing",
            icon: <PenTool size={18} />,
            label: "Answer Writing",
        },
        {
            path: "/dashboard/practice-pyq",
            icon: <Target size={18} />,
            label: "Practice PYQ",
        },
        {
            path: "/dashboard/current-affairs-quiz",
            icon: <Globe size={18} />,
            label: "Current Affairs Quiz",
        },
        {
            path: "/dashboard/mix-content",
            icon: <Mic size={18} />,
            label: "Interview Guidance",
        },
        {
            path: "/dashboard/support",
            icon: <HelpCircle size={18} />,
            label: "Support & Help",
        },
    ];

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col">
            {/* Mobile Menu Toggle Button */}
            <div className="lg:hidden w-full mx-auto flex justify-start pt-2">
                <button
                    onClick={() => setIsMenuOpen(true)}
                    className="p-2 flex items-center text-gray-700 focus:outline-none bg-white rounded-r-full shadow-md border border-gray-200 group"
                >
                    <span className="font-bold text-sm text-neutral-900 mt-1">
                        Dashboard
                    </span>
                    <ChevronRight
                        size={20}
                        className="text-gray-400 transition-transform group-hover:translate-x-1"
                    />
                </button>
            </div>

            <main className="grow w-full mx-auto flex flex-col lg:flex-row relative">
                {/* Mobile Overlay */}
                <AnimatePresence>
                    {isMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm z-40 lg:hidden"
                            onClick={handleOverlayClick}
                        />
                    )}
                </AnimatePresence>

                {/* Sidebar Navigation */}
                <aside
                    className={`fixed lg:sticky top-0 lg:top-32 left-0 h-full lg:h-fit w-70 lg:w-60 max-w-sm bg-white lg:bg-transparent z-50 lg:z-auto transition-transform duration-300 ease-in-out ${
                        isMenuOpen
                            ? "translate-x-0 pt-44"
                            : "-translate-x-full lg:translate-x-0"
                    }`}
                >
                    <div className="bg-white p-6 flex flex-col h-full overflow-y-auto custom-scrollbar">
                        {/* Mobile Close Button */}
                        <div className="lg:hidden flex justify-between items-center pb-4 border-b border-gray-100">
                            <span className="font-black text-neutral-900">
                                Dashboard
                            </span>
                            <button
                                onClick={() => setIsMenuOpen(false)}
                                className="text-gray-400 hover:text-red-700 transition-colors p-1"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Navigation Links */}
                        <nav className="flex flex-col gap-1.5 grow">
                            {navLinks.map((link, index) => {
                                const isActive =
                                    location.pathname === link.path;

                                // Common link classes
                                const baseClasses =
                                    "flex items-center w-full px-4 py-3 rounded-lg text-sm font-bold transition-all duration-300 group";
                                const activeClasses =
                                    "bg-yellow-400 text-black";
                                const inactiveClasses =
                                    "text-gray-500 hover:bg-gray-50 hover:text-red-700 hover:border-gray-200";

                                if (link.external) {
                                    return (
                                        <a
                                            key={index}
                                            href={getTestPortalUrl("/")}
                                            onClick={handleTestPortalClick}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`${baseClasses} ${inactiveClasses}`}
                                        >
                                            <span className="text-gray-700 group-hover:text-red-700 transition-colors">
                                                {link.icon}
                                            </span>
                                            <span className="ml-3 flex-1 text-left">
                                                {link.label}
                                            </span>
                                            <ExternalLink
                                                size={14}
                                                className="text-gray-300 group-hover:text-red-700 transition-colors"
                                            />
                                        </a>
                                    );
                                }

                                return (
                                    <Link
                                        key={index}
                                        to={link.path}
                                        onClick={() => setIsMenuOpen(false)} // Close menu on mobile after click
                                        className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
                                    >
                                        <span
                                            className={`${isActive ? "text-yellow-600" : "text-gray-400 group-hover:text-red-700"} transition-colors`}
                                        >
                                            {link.icon}
                                        </span>
                                        <span className="ml-3">
                                            {link.label}
                                        </span>
                                    </Link>
                                );
                            })}
                        </nav>

                        {/* Premium Profile Card */}
                        <div className="bg-neutral-900 rounded-xl p-5 my-4 text-center relative shadow-sm h-fit">
                            <div className="w-14 h-14 bg-white text-neutral-900 font-bold text-2xl rounded-full flex items-center justify-center mx-auto mb-3 shadow-inner">
                                {user?.name ? user.name[0].toUpperCase() : "S"}
                            </div>
                            <h3 className="text-white font-bold text-lg leading-tight truncate px-2">
                                {user?.name || "Student"}
                            </h3>
                            <p className="text-gray-400 text-xs font-medium truncate px-2 mt-1">
                                {user?.email?.toLowerCase() ||
                                    "student@example.com"}
                            </p>
                        </div>

                        {/* Logout Button */}
                        <div className="pt-4 border-t border-gray-100">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center justify-center px-4 py-3 bg-red-50 text-red-700 border border-red-100 font-bold rounded-lg hover:bg-red-700 hover:text-white hover:border-red-700 transition-colors duration-300 group"
                            >
                                <LogOut
                                    size={18}
                                    className="mr-2 group-hover:-translate-x-1 transition-transform"
                                />
                                Logout
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Main Content Area */}
                <section className="flex-1 w-full bg-gray-50 overflow-hidden min-h-125 my-5 lg:my-0 border-l border-gray-200">
                    {/* The Outlet renders the nested dashboard pages */}
                    <Outlet />
                </section>
            </main>
        </div>
    );
};

export default DashboardLayout;
